import { ReactNode, RefObject, useRef } from 'react'
import { debounce } from 'lodash'

import {
  Dropdown,
  DropdownItemProps,
  HStack,
  Spacer,
  Text,
  useToggle,
} from '@revolut-internal/ui-kit'

type NestedDropdownItemBaseProps<T> = Omit<
  DropdownItemProps,
  'value' | 'onChange' | 'children'
> & {
  label?: string
  value?: T[] | unknown
  renderValue?: (value?: T[] | unknown) => string
  children: (isShown: boolean, anchorRef: RefObject<HTMLDivElement | null>) => ReactNode
}

export const FILTERS_HOVER_DEBOUNCE = 250

export const NestedDropdownItemBase = <T,>({
  label,
  value,
  renderValue,
  children,
  ...dropdownItemProps
}: NestedDropdownItemBaseProps<T>) => {
  const anchorRef = useRef<HTMLDivElement>(null)

  const [isOpen, hoverStateActions] = useToggle({ defaultState: false })

  // debouncing actions, so we have smooth transitions + ability to cancel action
  const open = debounce(hoverStateActions.on, FILTERS_HOVER_DEBOUNCE)
  const close = debounce(hoverStateActions.off, FILTERS_HOVER_DEBOUNCE)

  const handleClose = () => {
    open.cancel()
    close()
  }
  const handleOpen = () => {
    close.cancel()
    open()
  }

  const renderValueLabel = (): string => {
    if (renderValue) {
      return renderValue(value) ?? ''
    }

    if (!Array.isArray(value)) {
      return ''
    }

    if (!value.length) {
      return ''
    }

    return String(value.length)
  }

  return (
    <Dropdown.Item
      use="button"
      variant="nested"
      aria-label={label}
      {...dropdownItemProps}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      ref={anchorRef}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
    >
      <HStack space="s-16" align="center">
        <Text variant="emphasis2" color="foreground">
          {label}
        </Text>

        <Spacer />

        <Text variant="emphasis2" color="grey-tone-50">
          {renderValueLabel()}
        </Text>
      </HStack>
      {children(isOpen, anchorRef)}
    </Dropdown.Item>
  )
}
